/**
 * Jungle camp definitions — positions extracted from walkability-grid2.json
 * (jungleCamp layer, bit 16), clustered & identified.
 *
 * Grid coordinates are 0-511 on the 512×512 walkability grid.
 */
import { JungleCamp, JungleCampType } from './simulationTypes';
import { jungleCampImages } from '../JungleSimulator/jungleCampImages';

/* ══════════════════ Image URL helper ══════════════════ */

/** Resolve a camp image key to a CDN URL */
export function campImageUrl(key: string): string {
  return (jungleCampImages as Record<string, string>)[key] ?? jungleCampImages.scuttle;
}

/* ══════════════════ Spawn / Respawn timers (seconds) ══════════════════ */

const T = {
  krugsSpawn: 67, // 1:07
  grompSpawn: 67, // 1:07
  smallCampSpawn: 55, // 0:55  (wolves, raptors, blue buff, red buff)
  smallCampRespawn: 135, // 2:15 (krugs, gromp, wolves, raptors)
  buffRespawn: 300, // 5:00  (blue buff, red buff — after kill)
  scuttleSpawn: 175, // 2:55
  scuttleRespawn: 150, // 2:30
  dragonSpawn: 300, // 5:00
  dragonRespawn: 300, // 5:00
  voidGrubsSpawn: 480, // 8:00
  riftHeraldSpawn: 900, // 15:00
  baronSpawn: 1200, // 20:00
  baronRespawn: 360, // 6:00
};

/* ══════════════════ Camp definition tuples ══════════════════ */

interface CampDef {
  id: string;
  type: JungleCampType;
  side: 'blue' | 'red' | 'neutral';
  name: string;
  gx: number;
  gy: number;
  initialSpawn: number;
  respawnTime: number;
  imageKey: string;
}

const CAMP_DEFS: CampDef[] = [
  /* ── Blue-side regular camps ── */
  { id: 'blue-krugs', type: 'krugs', side: 'blue', name: 'Krugs (Blue)', gx: 222, gy: 97, initialSpawn: T.krugsSpawn, respawnTime: T.smallCampRespawn, imageKey: 'krugs' },
  { id: 'blue-red_buff', type: 'red_buff', side: 'blue', name: 'Red Buff (Blue)', gx: 245, gy: 140, initialSpawn: T.smallCampSpawn, respawnTime: T.buffRespawn, imageKey: 'red' },
  { id: 'blue-raptors', type: 'raptors', side: 'blue', name: 'Raptors (Blue)', gx: 271, gy: 187, initialSpawn: T.smallCampSpawn, respawnTime: T.smallCampRespawn, imageKey: 'raptors' },
  { id: 'blue-wolves', type: 'wolves', side: 'blue', name: 'Wolves (Blue)', gx: 375, gy: 228, initialSpawn: T.smallCampSpawn, respawnTime: T.smallCampRespawn, imageKey: 'wolves' },
  { id: 'blue-blue_buff', type: 'blue_buff', side: 'blue', name: 'Blue Buff (Blue)', gx: 378, gy: 271, initialSpawn: T.smallCampSpawn, respawnTime: T.buffRespawn, imageKey: 'blue' },
  { id: 'blue-gromp', type: 'gromp', side: 'blue', name: 'Gromp (Blue)', gx: 433, gy: 293, initialSpawn: T.grompSpawn, respawnTime: T.smallCampRespawn, imageKey: 'gromp' },

  /* ── Red-side regular camps ── */
  { id: 'red-gromp', type: 'gromp', side: 'red', name: 'Gromp (Red)', gx: 79, gy: 221, initialSpawn: T.grompSpawn, respawnTime: T.smallCampRespawn, imageKey: 'gromp' },
  { id: 'red-blue_buff', type: 'blue_buff', side: 'red', name: 'Blue Buff (Red)', gx: 136, gy: 243, initialSpawn: T.smallCampSpawn, respawnTime: T.buffRespawn, imageKey: 'blue' },
  { id: 'red-wolves', type: 'wolves', side: 'red', name: 'Wolves (Red)', gx: 134, gy: 288, initialSpawn: T.smallCampSpawn, respawnTime: T.smallCampRespawn, imageKey: 'wolves' },
  { id: 'red-raptors', type: 'raptors', side: 'red', name: 'Raptors (Red)', gx: 240, gy: 324, initialSpawn: T.smallCampSpawn, respawnTime: T.smallCampRespawn, imageKey: 'raptors' },
  { id: 'red-red_buff', type: 'red_buff', side: 'red', name: 'Red Buff (Red)', gx: 270, gy: 371, initialSpawn: T.smallCampSpawn, respawnTime: T.buffRespawn, imageKey: 'red' },
  { id: 'red-krugs', type: 'krugs', side: 'red', name: 'Krugs (Red)', gx: 290, gy: 417, initialSpawn: T.krugsSpawn, respawnTime: T.smallCampRespawn, imageKey: 'krugs' },

  /* ── River — Scuttle Crab ── */
  { id: 'scuttle-top', type: 'scuttle', side: 'neutral', name: 'Scuttle (Top)', gx: 146, gy: 178, initialSpawn: T.scuttleSpawn, respawnTime: T.scuttleRespawn, imageKey: 'scuttle' },
  { id: 'scuttle-bot', type: 'scuttle', side: 'neutral', name: 'Scuttle (Bot)', gx: 365, gy: 334, initialSpawn: T.scuttleSpawn, respawnTime: T.scuttleRespawn, imageKey: 'scuttle' },

  /* ── Objectives ── */
  { id: 'dragon', type: 'dragon', side: 'neutral', name: 'Dragon', gx: 338, gy: 361, initialSpawn: T.dragonSpawn, respawnTime: T.dragonRespawn, imageKey: 'dragon' },
  { id: 'void-grubs', type: 'void_grubs', side: 'neutral', name: 'Void Grubs', gx: 173, gy: 154, initialSpawn: T.voidGrubsSpawn, respawnTime: 0, imageKey: 'voidgrubs' },
  { id: 'rift-herald', type: 'rift_herald', side: 'neutral', name: 'Rift Herald', gx: 173, gy: 154, initialSpawn: T.riftHeraldSpawn, respawnTime: 0, imageKey: 'herald' },
  { id: 'baron', type: 'baron', side: 'neutral', name: 'Baron Nashor', gx: 173, gy: 154, initialSpawn: T.baronSpawn, respawnTime: T.baronRespawn, imageKey: 'baron' },
];

/* ══════════════════ Factory ══════════════════ */

/** Create the initial set of jungle camps for the simulation. */
export function createJungleCamps(): JungleCamp[] {
  return CAMP_DEFS.map((d) => ({
    id: d.id,
    type: d.type,
    side: d.side,
    name: d.name,
    gx: d.gx,
    gy: d.gy,
    initialSpawn: d.initialSpawn,
    respawnTime: d.respawnTime,
    imageKey: d.imageKey,
    alive: false,
    nextSpawnAt: d.initialSpawn,
    killedAt: null,
  }));
}

/* ══════════════════ Baron-pit sequence IDs ══════════════════ */

/** The three camps that share baron pit, in chronological order. */
export const BARON_PIT_SEQUENCE = ['void-grubs', 'rift-herald', 'baron'] as const;
